import { Provider } from '../../types/shared.types';

/**
 * How long a marketplace takes to go live once it has been connected, as the
 * team reported it in Sep 2026. The wait is on the marketplace's side, so
 * these are what to tell a client, not something the app can measure.
 *
 * Read by the provider rows on Manage Providers and by the integration
 * playbook, which must not drift from each other.
 */
export type ProviderConnectionTiming = {
  /** Short enough for the playbook's timing column, which is headed. */
  label: string;
  /**
   * The same wait as a sentence. A provider row has no heading above it, so
   * the bare label there reads as an unexplained "2 days".
   */
  rowLabel: string;
  /** Why it takes that long. Shown in the playbook, not on the row. */
  note?: string;
};

/** Anything not named below connects straight away. */
export const DEFAULT_CONNECTION_TIMING: ProviderConnectionTiming = {
  label: 'Instant',
  rowLabel: 'Integration is instant',
};

export const PROVIDER_CONNECTION_TIMING: Partial<
  Record<Provider, ProviderConnectionTiming>
> = {
  [Provider.ubereats]: {
    label: 'Instant',
    rowLabel: 'Integration is instant',
  },
  [Provider.doordash]: {
    label: 'Up to 2 days',
    rowLabel: 'Integration takes up to 2 days',
    note: 'They review the store before it goes live.',
  },
  [Provider.grubhub]: {
    label: 'Instant',
    rowLabel: 'Integration is instant',
  },
  [Provider.grubhubPos]: {
    label: 'Instant',
    rowLabel: 'Integration is instant',
    note: 'A KitchenHub connection.',
  },
  [Provider.doordashPos]: {
    label: 'Instant',
    rowLabel: 'Integration is instant',
    note: 'A KitchenHub connection.',
  },
};

export const providerConnectionTiming = (
  provider: Provider
): ProviderConnectionTiming =>
  PROVIDER_CONNECTION_TIMING[provider] ?? DEFAULT_CONNECTION_TIMING;

/** The order the playbook lists them in: slowest first, so it reads as a warning. */
export const TIMING_TABLE_PROVIDERS: Provider[] = [
  Provider.doordash,
  Provider.grubhub,
  Provider.ubereats,
  Provider.grubhubPos,
  Provider.doordashPos,
];
